import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Zap } from '../lib/icons';
import SEOHead from '../components/SEOHead';
import ImageSlot from '../components/ImageSlot';
import ContentSlot from '../components/ContentSlot';
import { UPGRADE_SECTIONS, INSPIRATION_GALLERY, type UpgradeVariant, type ComparisonRow } from '../data/controller-upgrades';

// ── Icons ─────────────────────────────────────────────────────────────────────

const CheckIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 text-[#39FF14] shrink-0">
    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 00-1.414 0L8 12.586 4.707 9.293a1 1 0 00-1.414 1.414l4 4a1 1 0 001.414 0l8-8a1 1 0 000-1.414z" clipRule="evenodd" />
  </svg>
);

const CrossIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 text-gray-600 shrink-0">
    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
  </svg>
);

const ComparisonTable = ({
  headers,
  rows,
}: {
  headers: string[];
  rows: ComparisonRow[];
}) => (
  <div className="overflow-x-auto rounded-xl border border-gray-800 mt-8">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-gray-800">
          {headers.map((h, i) => (
            <th
              key={h}
              className={`px-5 py-3 text-left font-semibold ${i === 0 ? 'text-[#B0B0B0] w-40' : 'text-[#39FF14]'}`}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} className={`border-b border-gray-900 ${ri % 2 === 0 ? 'bg-gray-900/40' : ''}`}>
            <td className="px-5 py-3 text-[#B0B0B0] font-medium">{row.feature}</td>
            {row.cells.map((cell, ci) => (
              <td key={ci} className="px-5 py-3">
                {cell.value === true ? (
                  <CheckIcon />
                ) : cell.value === false ? (
                  <CrossIcon />
                ) : (
                  <span className="text-[#E0E0E0]">{cell.value}</span>
                )}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const VariantCard = ({
  variant,
  accent = '#39FF14',
}: {
  variant: UpgradeVariant;
  accent?: string;
}) => (
  <div
    className="relative flex flex-col bg-gray-900 rounded-2xl border border-gray-800 overflow-hidden transition-all duration-300 hover:border-opacity-60 hover:shadow-lg group"
    style={variant.isRecommended ? { borderColor: `${accent}50` } : {}}
  >
    {variant.isRecommended && (
      <div
        className="absolute top-3 right-3 text-[10px] font-black px-2.5 py-1 rounded-full uppercase tracking-wider z-10"
        style={{ backgroundColor: `${accent}20`, color: accent, border: `1px solid ${accent}40` }}
      >
        Recommended
      </div>
    )}

    {/* Media */}
    <div className="p-4 pb-0">
      <div className="aspect-video rounded-xl overflow-hidden bg-gray-950">
        <ImageSlot
          slotId={`perf-${variant.id}`}
          fallbackSrc={variant.media.videoFile ?? variant.media.photos[0] ?? ''}
          fallbackAlt={variant.name}
          className="object-cover"
          wrapperClassName="w-full h-full"
        />
      </div>
    </div>

    {/* Content */}
    <div className="p-6 flex flex-col flex-1">
      <h3 className="text-xl font-bold text-[#E0E0E0] mb-1 group-hover:text-[#39FF14] transition-colors">
        <ContentSlot
          slotId={`perf-${variant.id}-title`}
          fallback={variant.name}
          contentType="heading"
          pageSection={`performance-variant`}
          label={`${variant.name} — Card Title`}
        />
      </h3>
      <p className="text-sm text-[#B0B0B0] mb-4 leading-relaxed">
        <ContentSlot
          slotId={`perf-${variant.id}-tagline`}
          fallback={variant.tagline}
          contentType="subheading"
          pageSection="performance-variant"
          label={`${variant.name} — Tagline`}
        />
      </p>
      <p className="text-sm text-gray-500 mb-5 leading-relaxed">
        <ContentSlot
          slotId={`perf-${variant.id}-description`}
          fallback={variant.description}
          contentType="paragraph"
          pageSection="performance-variant"
          label={`${variant.name} — Description`}
        />
      </p>

      <ul className="space-y-2 mb-6 flex-1">
        {variant.benefits.map(b => (
          <li key={b} className="flex items-start gap-2 text-sm text-[#B0B0B0]">
            <CheckIcon />
            <span>{b}</span>
          </li>
        ))}
      </ul>

      <div className="pt-4 border-t border-gray-800 flex items-center justify-between">
        <span className="text-xs text-gray-600 italic">
          {variant.price !== null
            ? `${variant.pricePrefix ?? ''} $${variant.price}`
            : 'Pricing from Google Sheet'}
        </span>
        <Link
          to="/book-repair"
          className="text-xs font-bold text-[#39FF14] hover:underline flex items-center gap-1"
        >
          Get a Quote <ChevronRight className="h-3 w-3" />
        </Link>
      </div>
    </div>
  </div>
);

// ── Sticky section nav ────────────────────────────────────────────────────────

const SectionNav = ({ activeId }: { activeId: string }) => {
  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <nav className="space-y-1">
      <p className="text-[10px] font-black text-gray-600 uppercase tracking-widest mb-4 px-3">Upgrades</p>
      {UPGRADE_SECTIONS.filter(s => s.isActive).map(s => {
        const isActive = activeId === s.id;
        return (
          <button
            key={s.id}
            onClick={() => handleClick(s.id)}
            className={`w-full text-left px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 flex items-center gap-2 ${
              isActive
                ? 'bg-[#39FF14]/10 text-[#39FF14] border border-[#39FF14]/20'
                : 'text-[#B0B0B0] hover:text-[#E0E0E0] hover:bg-gray-900'
            }`}
          >
            {isActive && <span className="w-1.5 h-1.5 rounded-full bg-[#39FF14] shrink-0" />}
            {s.navLabel}
          </button>
        );
      })}
      <div className="pt-4">
        <Link
          to="/book-repair"
          className="flex items-center justify-center gap-1.5 w-full bg-[#39FF14] hover:bg-[#00BFFF] text-black font-black text-xs px-4 py-2.5 rounded-lg transition-colors"
        >
          <Zap className="h-3.5 w-3.5" />
          Start My Build
        </Link>
      </div>
    </nav>
  );
};

// ── Mobile pill nav ───────────────────────────────────────────────────────────

const MobilePillNav = ({ activeId }: { activeId: string }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const handleClick = (id: string) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div
      ref={scrollRef}
      className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide"
      style={{ scrollbarWidth: 'none' }}
    >
      {UPGRADE_SECTIONS.filter(s => s.isActive).map(s => (
        <button
          key={s.id}
          onClick={() => handleClick(s.id)}
          className={`whitespace-nowrap px-4 py-1.5 rounded-full text-xs font-bold border transition-all shrink-0 ${
            activeId === s.id
              ? 'bg-[#39FF14] text-black border-[#39FF14]'
              : 'text-[#B0B0B0] border-gray-700 hover:border-gray-500'
          }`}
        >
          {s.navLabel}
        </button>
      ))}
    </div>
  );
};

// ── Main page ─────────────────────────────────────────────────────────────────

const PerformanceUpgradesPage = () => {
  const [activeSection, setActiveSection] = useState(UPGRADE_SECTIONS[0]?.id ?? '');

  // Scrollspy — track which section is in the top 40% of the viewport
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) setActiveSection(entry.target.id);
        });
      },
      { rootMargin: '-15% 0px -65% 0px' },
    );

    UPGRADE_SECTIONS.forEach(s => {
      const el = document.getElementById(s.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const jsonLD = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Controller Performance Upgrades | GameFix Solutions',
    description: 'TMR joysticks, mouse click triggers, back paddles, custom shells and more. Build the controller you actually want.',
    url: 'https://gamefixsolutions.com/performance-upgrades',
  };

  return (
    <div className="bg-[#0D0D0D] min-h-screen">
      <SEOHead
        title="Controller Performance Upgrades | TMR, Mouse Click, Paddles | GameFix Solutions"
        description="TMR magnetic joysticks, mouse click triggers, back paddles, custom shells — every upgrade explained. Build the controller you actually want at GameFix Solutions."
        keywords="tmr joystick upgrade, mouse click triggers ps5, back paddles ps5, custom ps5 controller, gulikit tmr, k-silver tmr, rise4 paddles, controller upgrades san diego"
        canonical="https://gamefixsolutions.com/performance-upgrades"
        jsonLD={jsonLD}
      />

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-b from-black via-[#050505] to-[#0D0D0D] pt-24 pb-20 border-b border-gray-900">
        {/* Grid pattern */}
        <div className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(#39FF14 1px, transparent 1px), linear-gradient(90deg, #39FF14 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        {/* Glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-[#39FF14]/5 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 bg-[#39FF14]/10 border border-[#39FF14]/30 rounded-full px-5 py-1.5 mb-8">
            <Zap className="h-3.5 w-3.5 text-[#39FF14]" />
            <span className="text-[#39FF14] font-black text-xs tracking-widest uppercase">Performance Upgrades</span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-[#E0E0E0] leading-[1.05] mb-6 tracking-tight">
            <ContentSlot
              slotId="perf-hero-title"
              fallback="Build the Controller"
              contentType="heading"
              pageSection="performance-hero"
              label="Hero Headline (Line 1)"
            /><br />
            <span className="text-[#39FF14]">
              <ContentSlot
                slotId="perf-hero-title-green"
                fallback="You Actually Want"
                contentType="heading"
                pageSection="performance-hero"
                label="Hero Headline (Line 2 — green)"
              />
            </span>
          </h1>

          <p className="text-lg md:text-xl text-[#B0B0B0] max-w-2xl mx-auto mb-10 leading-relaxed">
            <ContentSlot
              slotId="perf-hero-subtitle"
              fallback="Choose the upgrades that fit your play style — from drift-proof joysticks to professional back paddles. Every mod explained. Every benefit justified."
              contentType="paragraph"
              pageSection="performance-hero"
              label="Hero Subtitle"
            />
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/book-repair"
              className="inline-flex items-center justify-center gap-2 bg-[#39FF14] hover:bg-[#00BFFF] text-black font-black px-8 py-4 rounded-xl transition-all text-base shadow-lg shadow-[#39FF14]/20"
            >
              <Zap className="h-5 w-5" />
              <ContentSlot slotId="perf-hero-cta-primary" fallback="Customize My Controller" contentType="button" pageSection="performance-hero" label="Primary CTA Button" />
            </Link>
            <Link
              to="/custom-controllers"
              className="inline-flex items-center justify-center gap-2 bg-gray-900 hover:bg-gray-800 text-[#E0E0E0] font-semibold px-8 py-4 rounded-xl border border-gray-700 hover:border-[#39FF14]/50 transition-all text-base"
            >
              <ContentSlot slotId="perf-hero-cta-secondary" fallback="See Completed Builds" contentType="button" pageSection="performance-hero" label="Secondary CTA Button" /> <ChevronRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── Mobile nav ────────────────────────────────────────────────────── */}
      <div className="sticky top-16 z-40 bg-[#0D0D0D]/95 backdrop-blur border-b border-gray-900 px-4 py-3 lg:hidden">
        <MobilePillNav activeId={activeSection} />
      </div>

      {/* ── Body: sidebar + sections ──────────────────────────────────────── */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex gap-10 items-start">

          {/* Sticky sidebar nav — desktop only */}
          <aside className="hidden lg:block w-52 shrink-0 sticky top-28 self-start">
            <SectionNav activeId={activeSection} />
          </aside>

          {/* Sections */}
          <main className="flex-1 min-w-0 space-y-24">

            {UPGRADE_SECTIONS.filter(s => s.isActive)
              .sort((a, b) => a.displayOrder - b.displayOrder)
              .map(section => (
                <section key={section.id} id={section.id} className="scroll-mt-28">

                  {/* Section header */}
                  <div className="mb-10">
                    <div className="inline-flex items-center gap-2 bg-[#39FF14]/8 border border-[#39FF14]/20 rounded-full px-4 py-1 mb-4">
                      <span className="text-[#39FF14] font-bold text-xs tracking-wider uppercase">
                        {section.navLabel}
                      </span>
                    </div>
                    <h2 className="text-3xl md:text-4xl font-black text-[#E0E0E0] mb-3 leading-tight">
                      <ContentSlot
                        slotId={`perf-${section.id}-title`}
                        fallback={section.title}
                        contentType="heading"
                        pageSection={`performance-${section.id}`}
                        label={`${section.navLabel} — Section Title`}
                      />
                    </h2>
                    <p className="text-[#39FF14] font-semibold text-lg mb-4">
                      <ContentSlot
                        slotId={`perf-${section.id}-subtitle`}
                        fallback={section.subtitle}
                        contentType="subheading"
                        pageSection={`performance-${section.id}`}
                        label={`${section.navLabel} — Subtitle`}
                      />
                    </p>
                    <p className="text-[#B0B0B0] leading-relaxed max-w-3xl">
                      <ContentSlot
                        slotId={`perf-${section.id}-description`}
                        fallback={section.description}
                        contentType="paragraph"
                        pageSection={`performance-${section.id}`}
                        label={`${section.navLabel} — Description`}
                      />
                    </p>
                  </div>

                  {/* Variant cards */}
                  <div
                    className={`grid gap-6 ${
                      section.variants.length === 1
                        ? 'grid-cols-1 max-w-xl'
                        : section.variants.length === 2
                        ? 'grid-cols-1 sm:grid-cols-2'
                        : section.variants.length >= 3
                        ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3'
                        : 'grid-cols-1'
                    }`}
                  >
                    {section.variants
                      .filter(v => v.isActive)
                      .sort((a, b) => a.displayOrder - b.displayOrder)
                      .map(v => (
                        <VariantCard key={v.id} variant={v} />
                      ))}
                  </div>

                  {/* Comparison table */}
                  {section.comparisonHeaders && section.comparisonRows && (
                    <ComparisonTable
                      headers={section.comparisonHeaders}
                      rows={section.comparisonRows}
                    />
                  )}

                  {/* Section CTA */}
                  <div className="mt-10 flex items-center gap-4">
                    <Link
                      to={section.ctaHref}
                      className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-[#00BFFF] text-black font-black px-6 py-3 rounded-xl transition-all text-sm"
                    >
                      {section.ctaLabel} <ChevronRight className="h-4 w-4" />
                    </Link>
                    <Link
                      to="/book-repair"
                      className="text-sm font-semibold text-[#B0B0B0] hover:text-[#39FF14] transition-colors"
                    >
                      Or ask us what's right for you →
                    </Link>
                  </div>

                  {/* Divider */}
                  <div className="mt-24 h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent" />
                </section>
              ))}

            {/* ── Section 6: Build Inspiration ──────────────────────────── */}
            <section id="inspiration" className="scroll-mt-28">
              <div className="mb-10">
                <div className="inline-flex items-center gap-2 bg-[#39FF14]/8 border border-[#39FF14]/20 rounded-full px-4 py-1 mb-4">
                  <span className="text-[#39FF14] font-bold text-xs tracking-wider uppercase">Gallery</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-black text-[#E0E0E0] mb-3 leading-tight">
                  See These Upgrades in Real Builds
                </h2>
                <p className="text-[#39FF14] font-semibold text-lg mb-4">
                  Every one of these started as a stock controller.
                </p>
                <p className="text-[#B0B0B0] leading-relaxed max-w-2xl">
                  TMR joysticks, custom shells, click upgrades, paddles — all of these builds came
                  through our shop. Click any controller to see its full build summary and
                  upgrades installed.
                </p>
              </div>

              {/* Gallery grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {INSPIRATION_GALLERY.map((item, i) => (
                  <div
                    key={i}
                    className="group relative aspect-square bg-gray-900 rounded-xl overflow-hidden border border-gray-800 hover:border-[#39FF14]/50 transition-all duration-300"
                  >
                    <ImageSlot
                      slotId={`perf-gallery-${i}`}
                      fallbackSrc={item.src}
                      fallbackAlt={item.label}
                      className="object-cover group-hover:scale-105 transition-transform duration-500"
                      wrapperClassName="w-full h-full"
                    />
                    <Link
                      to="/custom-controllers"
                      className="absolute inset-0 z-[5] flex items-end p-3"
                      tabIndex={-1}
                    >
                      <div className="w-full bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pb-1">
                        <span className="text-[#39FF14] text-xs font-bold">{item.label}</span>
                      </div>
                    </Link>
                  </div>
                ))}
              </div>

              <div className="mt-10 flex items-center gap-4">
                <Link
                  to="/custom-controllers"
                  className="inline-flex items-center gap-2 bg-[#39FF14] hover:bg-[#00BFFF] text-black font-black px-6 py-3 rounded-xl transition-all text-sm"
                >
                  Full Controller Gallery <ChevronRight className="h-4 w-4" />
                </Link>
              </div>
            </section>

            {/* ── Final CTA ─────────────────────────────────────────────── */}
            <section className="bg-gradient-to-br from-gray-900 to-black rounded-3xl border border-gray-800 p-10 text-center">
              <Zap className="h-10 w-10 text-[#39FF14] mx-auto mb-4" />
              <h2 className="text-3xl font-black text-[#E0E0E0] mb-3">
                Ready to Build Yours?
              </h2>
              <p className="text-[#B0B0B0] mb-8 max-w-xl mx-auto leading-relaxed">
                Book a service and tell us which upgrades you want. Drop off in San Diego or
                Temecula, or mail in from anywhere in the US. We will take care of the rest.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/book-repair"
                  className="inline-flex items-center justify-center gap-2 bg-[#39FF14] hover:bg-[#00BFFF] text-black font-black px-8 py-4 rounded-xl transition-all shadow-lg shadow-[#39FF14]/10"
                >
                  <Zap className="h-5 w-5" /> Book My Build
                </Link>
                <Link
                  to="/mail-in-repairs"
                  className="inline-flex items-center justify-center gap-2 bg-gray-800 hover:bg-gray-700 text-[#E0E0E0] font-semibold px-8 py-4 rounded-xl border border-gray-700 hover:border-[#39FF14]/50 transition-all"
                >
                  Mail It In
                </Link>
              </div>
            </section>

          </main>
        </div>
      </div>
    </div>
  );
};

export default PerformanceUpgradesPage;
